import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { EnvironmentService } from 'src/app/core/enviroment/environment.service';
import { Observable, Subject } from 'rxjs';

import {
  EditFacCombosModel,
  FullFacModel,
  NotesUpdateModel,
  EditFacSearchProfileModel,
  DiscardModel,
  AutocompleteTransferModel,
  TransferFormModel,
  TransferModel,
  AutocompleteProductResultsModel,
  AutocompleteProductAddressResultModel} from '../../../../core/models/edit-fac-modal.model';
import { ProductSearchModel } from '../modal-product-search/product-search.model';
import { Phone } from 'src/app/core/models/phone.model';

@Injectable({
  providedIn: 'root'
})
export class EditFacModalService {

  private uri: string = this.environmentService.getEnvVar('api_facs');
  private uriv2: string = this.environmentService.getEnvVar('api_facs_v2');
  private loadingState = new Subject<boolean>();

  public clearProductsModal: Subject<boolean> = new Subject<boolean>();

  private featureTransferFac: boolean = this.environmentService.getEnvVar('feature_transfer_fac') === 'true';
  public featureOperationMarkets: boolean = this.environmentService.getEnvVar('feature_operation_markets') === 'true';

  constructor(
    private http: HttpClient,
    private environmentService: EnvironmentService
  ) { }

  public getLoading() {
    return this.loadingState.asObservable();
  }

  public setLoader(state: boolean) {
    this.loadingState.next(state);
  }

  public getClearProductsModal(): Observable<boolean> {
    return this.clearProductsModal.asObservable();
  }

  public setClearProductsModal(): void {
    this.clearProductsModal.next(true);
  }

  public getCombos(): Observable<EditFacCombosModel> {
    return this.http.get<EditFacCombosModel>(`${this.uri}/fac/combos`);
  }

  public getFacById(id: string): Observable<FullFacModel> {
    return this.http.get<FullFacModel>(`${this.uriv2}/fac/${id}`);
  }

  public callWhatsApp(phone: string) {
    window.open(`https://api.whatsapp.com/send?phone=${this.sanitizePhoneNumber(phone)}`, '_blank');
  }

  public sanitizePhoneNumber(phone : string): string {
    if (!phone) {
      return;
    }
    return phone.replace(/[^0-9]/g,'');
  }

  public searchProducts(searchObj: ProductSearchModel) {
    return this.http.request('POST', `${this.uri}/search/products`, {
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: JSON.stringify(searchObj),
    });
  }

  public patchFacById(id: string, bodyRequest: any): Observable<any> {
    return this.http.patch<any>(`${this.uri}/fac/${id}`, bodyRequest
    );
  }

  public updateFacPhone(id: string, phone: Phone): Observable<any> {
    const bodyRequest = {
      alternative_phone: phone
    };
    return this.http.put<any>(`${this.uri}/fac/${id}/phone`, bodyRequest);
  }

  public updateFacEmail(id: string, email: string): Observable<any> {
    const bodyRequest = {
      alternative_email: email
    };
    return this.http.put<any>(`${this.uri}/fac/${id}/email`, bodyRequest);
  }

  public updateFacNotes(id: string, body: NotesUpdateModel): Observable<any> {
    return this.http.post<any>(`${this.uri}/fac/${id}/notes`, body);
  }

  public getFacHistory(id: string) {
    return this.http.get<any>(`${this.uriv2}/fac/${id}/history?` + Math.random());
  }

  public deleteFacSearchProfile(facId: string, profileId: string): Observable<any> {
    return this.http.delete<any>(`${this.uri}/fac/${facId}/search-profile/${profileId}`);
  }

  public postFacSearchProfile(facId: string, body: EditFacSearchProfileModel): Observable<any> {
    return this.http.post<any>(`${this.uri}/fac/${facId}/search-profile`, body);
  }

  public patchFacSearchProfile(facId: string, profileId: string, body: EditFacSearchProfileModel): Observable<any> {
    return this.http.patch<any>(`${this.uri}/fac/${facId}/search-profile/${profileId}`, body);
  }

  public putFacArchive(facId: string, state: boolean): Observable<any> {
    const body = {
      archived: state
    };
    return this.http.put<any>(`${this.uri}/fac/${facId}/archive`, body);
  }

  public putFacDiscard(facId: string, body: DiscardModel): Observable<any> {
    return this.http.put<any>(`${this.uri}/fac/${facId}/discard`, body);
  }

  public getFacAddress(country: string, zipCode: string): Observable<any> {
    return this.http.get<any>(`${this.uri}/search/addresses/${country}/${zipCode}`);
  }

  public autoCompleteTransfer(data: string): Observable<AutocompleteTransferModel[]> {
    const searchObj = {
      search: data
    };
    if (this.featureTransferFac) {
      const v2Url: string = this.uri.substr(0, this.uri.length - 2);
      return this.http.post<AutocompleteTransferModel[]>(`${v2Url}v2/team/members`, searchObj);
    } else {
      return this.http.post<AutocompleteTransferModel[]>(`${this.uri}/team/members/`, searchObj);
    }
  }

  public postFacTransfer(facId: string, data: TransferFormModel): Observable<any> {
    const body: TransferModel = {
      destination_user: data
    };
    return this.http.post<any>(`${this.uri}/fac/${facId}/transfer/`, body);
  }

  public checkOpMarkets() {
    return this.http.get<any>(`${this.uri}/operation-markets`);
  }

  public autoCompleteProduct(name: string, page: number, rows: number): Observable<AutocompleteProductResultsModel> {
    const searchObj = {
      product_type: 'REAL_ESTATE_PARENT',
      page_number: page,
      row_number: rows,
      direction: 'ASC',
      input: name
    };
    return this.http.post<AutocompleteProductResultsModel>(`${this.uri}/products/autocomplete`, searchObj);
  }

  public autoCompleteProductAddress(name: string, page: number, rows: number): Observable<AutocompleteProductAddressResultModel[]> {
    const searchObj = {
      product_type: 'REAL_ESTATE',
      page_number: page,
      row_number: rows,
      direction: 'ASC',
      input: name
    };
    return this.http.post<AutocompleteProductAddressResultModel[]>(`${this.uri}/products/address/autocomplete`, searchObj);
  }

  public searchProductsRealEstateParent(searchTxt: any, page: number, rows: number): Observable<AutocompleteProductResultsModel> {
    const searchObj = {
      page_number: page,
      row_number: rows,
      lancamentos: {
        search: searchTxt
      }
    };
    return this.http.post<AutocompleteProductResultsModel>(`${this.uri}/search/products`, searchObj);
  }

  public searchProductsRealEstate(params: any, page: number, rows: number): Observable<AutocompleteProductResultsModel> {
    const searchObj = {
      page_number: page,
      row_number: rows,
      prontos: {
        cod: params.cod,
        address: params.address,
        num: params.num
      }
    };
    return this.http.post<AutocompleteProductResultsModel>(`${this.uri}/search/products`, searchObj);
  }

  getColumnName(value) {
    switch (value) {
      case 1:
        return 'Sem atendimento';
      case 2:
        return 'Sem contato';
      case 3:
        return 'Em atendimento';
      case 4:
        return 'Visita';
      case 5:
        return 'Proposta';
      case 6:
        return 'Venda';
      default:
        return 0;
    }
  }

  getColumnType(value) {
    switch (value.toLowerCase()) {
      case 'sem atendimento':
        return 1;
      case 'sem contato':
        return 2;
      case 'em atendimento':
        return 3;
      case 'visita':
        return 4;
      case 'proposta':
        return 5;
      case 'venda':
        return 6;
      default:
        return 0;
    }
  }

  public getStates(country: string) {
    return this.http.get(`${this.uri}/combo/${country}/states`);
  }

  public getLocations(data: string) {
    return this.http.get(`${this.uri}/combo/${data}/locations`);
  }

  public getNeighborhoods(data: string) {
    return this.http.get(`${this.uri}/combo/${data}/neighborhoods`);
  }

  public getProductTypes() {
    return this.http.get(`${this.uri}/combo/product-types`);
  }

  public hrefLink(facId: string, profileId: string) {
    return this.http.post(`${this.uri}/fac/portal/url/${facId}/${profileId}`, {});
  }

  public getOfferedProduct(facId: string) {
    let params = new HttpParams();
    params = params.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
    return this.http.get(`${this.uriv2}/fac/${facId}/offered-products`, {params});
  }

  public getChatCombos(facId: string) {
    return this.http.get<any>(`${this.uri}/fac/${facId}/predefined-messages`);
  }

  public resendChatEmail(facId: string, messageId: string) {
    return this.http.post<any>(`${this.uri}/fac/${facId}/resend-chat-message`, {chat_id: messageId});
  }
}
